import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { useAuth } from "../../../contexts/AuthContext";

import ServiceHourTracking from "./ServiceHourTracking";
import RoleAssignment from "./RoleAssignment";
import { distributionApi } from "../../../services/api";

const EmployeeDetailPanel = ({ employee, isOpen, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState("details");
  const [isEditing, setIsEditing] = useState(false);
  const [editedEmployee, setEditedEmployee] = useState(null);
  const [troncSummary, setTroncSummary] = useState(null);
  const [troncAllocations, setTroncAllocations] = useState([]);
  const [troncLoading, setTroncLoading] = useState(false);
  const { canEditEmployees } = useAuth();

  // Check if PII is masked from the API response
  const piiMasked = employee?._raw?.piiMasked || employee?.piiMasked || false;

  useEffect(() => {
    if (employee) {
      setEditedEmployee({ ...employee });
    }
  }, [employee]);

  // Fetch tronc summary when employee changes
  useEffect(() => {
    const fetchTroncSummary = async () => {
      if (!employee?.id) return;

      setTroncLoading(true);
      try {
        // Fetch both summary and allocations in parallel
        const [summaryResult, allocationsResult] = await Promise.all([
          distributionApi.getEmployeeTroncSummary(employee.id, {
            // Get last 90 days
            fromDate: new Date(
              Date.now() - 90 * 24 * 60 * 60 * 1000,
            ).toISOString(),
          }),
          distributionApi.getEmployeeAllocations(employee.id, {}),
        ]);

        if (summaryResult.success) {
          setTroncSummary(summaryResult.data);
        }
        if (allocationsResult.success) {
          setTroncAllocations(allocationsResult.data || []);
        }
      } catch (err) {
        console.error("Failed to fetch tronc data:", err);
      } finally {
        setTroncLoading(false);
      }
    };

    if (isOpen && employee?.id) {
      fetchTroncSummary();
    }
  }, [employee?.id, isOpen]);

  const handleSave = () => {
    onUpdate(editedEmployee);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedEmployee({ ...employee });
    setIsEditing(false);
  };

  if (!employee) return null;

  const tabs = [
    { id: "details", label: "Details", icon: "User" },
    { id: "tronc", label: "TRONC", icon: "PoundSterling" },
    { id: "role", label: "Role Assignment", icon: "Briefcase" },
    { id: "hours", label: "Service Hours", icon: "Clock" },
  ];

  const getEligibilityBadge = (status) => {
    const badges = {
      Eligible: {
        color: "var(--color-success)",
        bg: "rgba(34, 197, 94, 0.1)",
        text: "Eligible",
        icon: "CheckCircle",
      },
      "Not Eligible": {
        color: "var(--color-error)",
        bg: "rgba(239, 68, 68, 0.1)",
        text: "Not Eligible",
        icon: "XCircle",
      },
      Probationary: {
        color: "var(--color-accent)",
        bg: "rgba(251, 146, 60, 0.1)",
        text: "Probationary",
        icon: "AlertCircle",
      },
    };
    return badges?.[status] || badges?.["Not Eligible"];
  };

  const badge = getEligibilityBadge(employee?.eligibilityStatus);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <>
      {/* Overlay - Higher z-index than sidebar */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 z-[1100] ${
          isOpen ? "opacity-50" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Panel - Higher z-index to sit above overlay and sidebar */}
      <div
        className={`fixed right-0 top-0 h-full w-full md:w-2/3 lg:w-1/2 bg-white shadow-2xl transform transition-transform duration-300 z-[1150] overflow-y-auto ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header - Sticky with high z-index */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-[1160]">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <img
                src={employee?.photo}
                alt={`${employee?.name} profile photo`}
                className="w-16 h-16 rounded-full object-cover"
              />
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {employee?.name}
                </h2>
                <p className="text-sm text-gray-500">{employee?.email}</p>
                <div className="mt-2 flex items-center gap-3">
                  <span
                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium"
                    style={{
                      color: badge?.color,
                      backgroundColor: badge?.bg,
                    }}
                  >
                    <Icon name={badge?.icon} size={12} />
                    {badge?.text}
                  </span>
                  {troncSummary && troncSummary.totalTronc > 0 && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      <Icon name="PoundSterling" size={12} />
                      {formatCurrency(troncSummary.totalTronc)} (90 days)
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              iconName="X"
              onClick={onClose}
              className="flex-shrink-0"
            />
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 border-b border-gray-200">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === tab?.id
                    ? "border-b-2 text-primary"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                style={{
                  borderColor:
                    activeTab === tab?.id
                      ? "var(--color-primary)"
                      : "transparent",
                }}
              >
                <Icon name={tab?.icon} size={16} />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {activeTab === "details" && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Employee Details
                </h3>
                {!isEditing && canEditEmployees ? (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Edit"
                    onClick={() => setIsEditing(true)}
                  >
                    Edit Details
                  </Button>
                ) : isEditing ? (
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={handleCancel}>
                      Cancel
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      iconName="Save"
                      onClick={handleSave}
                    >
                      Save Changes
                    </Button>
                  </div>
                ) : null}
              </div>

              {/* PII Restricted Banner */}
              {piiMasked && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                  <Icon name="ShieldAlert" size={16} className="shrink-0" />
                  <span>Some personal information is hidden for privacy. Only tenant administrators can view full employee details.</span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  {isEditing ? (
                    <Input
                      value={editedEmployee?.name}
                      onChange={(e) =>
                        setEditedEmployee({
                          ...editedEmployee,
                          name: e?.target?.value,
                        })
                      }
                    />
                  ) : (
                    <p className="text-gray-900">{employee?.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  {isEditing ? (
                    <Input
                      type="email"
                      value={editedEmployee?.email}
                      onChange={(e) =>
                        setEditedEmployee({
                          ...editedEmployee,
                          email: e?.target?.value,
                        })
                      }
                    />
                  ) : piiMasked ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="password"
                        value={employee?.email || ""}
                        readOnly
                        className="bg-gray-100 border border-gray-200 rounded-md px-3 py-1.5 text-sm text-gray-500 w-full cursor-not-allowed"
                        tabIndex={-1}
                      />
                      <Icon name="Lock" size={14} className="text-gray-400 shrink-0" />
                    </div>
                  ) : (
                    <p className="text-gray-900">{employee?.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  {isEditing ? (
                    <Input
                      type="tel"
                      value={editedEmployee?.phone}
                      onChange={(e) =>
                        setEditedEmployee({
                          ...editedEmployee,
                          phone: e?.target?.value,
                        })
                      }
                    />
                  ) : piiMasked ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="password"
                        value={employee?.phone || "••••••"}
                        readOnly
                        className="bg-gray-100 border border-gray-200 rounded-md px-3 py-1.5 text-sm text-gray-500 w-full cursor-not-allowed"
                        tabIndex={-1}
                      />
                      <Icon name="Lock" size={14} className="text-gray-400 shrink-0" />
                    </div>
                  ) : (
                    <p className="text-gray-900">{employee?.phone || "—"}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <p className="text-gray-900">{employee?.role}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employment Type
                  </label>
                  <p className="text-gray-900">{employee?.employmentType}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contracted Hours
                  </label>
                  <p className="text-gray-900">
                    {employee?.serviceHours ? `${employee.serviceHours}h / week` : "—"}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <p className="text-gray-900">
                    {employee?.startDate ? new Date(employee.startDate).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—"}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tenure
                  </label>
                  <p className="text-gray-900">
                    {(() => {
                      if (!employee?.startDate) return "—";
                      const days = Math.floor((new Date() - new Date(employee.startDate)) / (1000 * 60 * 60 * 24));
                      const years = Math.floor(days / 365);
                      const months = Math.floor((days % 365) / 30);
                      const remaining = days % 30;
                      const parts = [];
                      if (years > 0) parts.push(`${years}y`);
                      if (months > 0) parts.push(`${months}m`);
                      if (remaining > 0 || parts.length === 0) parts.push(`${remaining}d`);
                      return parts.join(" ") + ` (${days} days)`;
                    })()}
                  </p>
                </div>
              </div>

              {/* Probation Status */}
              {employee?.startDate && (
                <div className={`rounded-lg border p-4 ${
                  employee?.isEligibleForTronc
                    ? "bg-green-50 border-green-200"
                    : "bg-yellow-50 border-yellow-200"
                }`}>
                  <div className="flex items-start gap-3">
                    <Icon
                      name={employee?.isEligibleForTronc ? "CheckCircle" : "Clock"}
                      size={20}
                      className={employee?.isEligibleForTronc ? "text-success mt-0.5" : "text-warning mt-0.5"}
                    />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">
                        Probation Status
                      </h4>
                      <p className="text-sm text-gray-700">
                        {(() => {
                          // Use API-provided probation data if available from raw employee
                          const rawEmp = employee?._raw;
                          const probDays = rawEmp?.probationPeriodDays;
                          const passed = rawEmp?.hasPassedProbation ?? true;
                          if (!probDays || probDays <= 0) {
                            return "No probation period configured — employee is eligible.";
                          }
                          if (passed) {
                            return `Probation period of ${probDays} days has been completed.`;
                          }
                          const startD = new Date(employee.startDate);
                          const endD = new Date(startD);
                          endD.setDate(endD.getDate() + probDays);
                          const daysLeft = Math.max(0, Math.ceil((endD - new Date()) / (1000 * 60 * 60 * 24)));
                          return `In probation — ${daysLeft} days remaining (ends ${endD.toLocaleDateString("en-GB")}).`;
                        })()}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "tronc" && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">
                TRONC Summary
              </h3>

              {troncLoading ? (
                <div className="text-center py-8">
                  <div className="spinner mx-auto mb-4" />
                  <p className="text-muted-foreground">Loading TRONC data...</p>
                </div>
              ) : !employee?.isEligibleForTronc ? (
                <div className="bg-warning/10 border border-warning/20 rounded-lg p-6 text-center">
                  <Icon
                    name="AlertTriangle"
                    size={32}
                    className="mx-auto mb-3 text-warning"
                  />
                  <h4 className="font-semibold mb-2">Not Eligible for TRONC</h4>
                  <p className="text-sm text-muted-foreground">
                    This employee is not currently eligible for TRONC
                    distributions.
                  </p>
                </div>
              ) : (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-success/10 rounded-lg p-4 text-center">
                      <p className="text-sm text-muted-foreground mb-1">
                        Total TRONC (90 days)
                      </p>
                      <p className="text-3xl font-bold text-success font-data">
                        {formatCurrency(troncSummary?.totalTronc || 0)}
                      </p>
                    </div>
                    <div className="bg-primary/10 rounded-lg p-4 text-center">
                      <p className="text-sm text-muted-foreground mb-1">
                        Distributions
                      </p>
                      <p className="text-3xl font-bold text-primary font-data">
                        {troncSummary?.transactionCount || 0}
                      </p>
                    </div>
                    <div className="bg-muted rounded-lg p-4 text-center">
                      <p className="text-sm text-muted-foreground mb-1">
                        All-Time Allocations
                      </p>
                      <p className="text-3xl font-bold text-foreground font-data">
                        {troncAllocations.length}
                      </p>
                    </div>
                  </div>

                  {/* Average Calculation */}
                  {troncSummary?.transactionCount > 0 && (
                    <div className="p-4 bg-muted rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                          Average per distribution (90 days)
                        </span>
                        <span className="font-semibold font-data">
                          {formatCurrency(
                            troncSummary.totalTronc /
                              troncSummary.transactionCount,
                          )}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Recent Transactions from summary */}
                  {troncSummary?.transactions && troncSummary.transactions.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-md font-semibold text-gray-900 mb-4">
                        Recent TRONC Distributions (90 Days)
                      </h4>
                      <div className="space-y-3">
                        {troncSummary.transactions.map((txn, idx) => (
                          <div
                            key={txn.id || idx}
                            className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center">
                                <Icon
                                  name="PoundSterling"
                                  size={20}
                                  className="text-success"
                                />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">
                                  {formatDate(txn.date)}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {txn.description ||
                                    txn.location ||
                                    "TRONC Payment"}
                                </p>
                              </div>
                            </div>
                            <p className="text-xl font-bold font-data text-success">
                              {formatCurrency(txn.amount)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Full Distribution History */}
                  <div className="mt-6">
                    <h4 className="text-md font-semibold text-gray-900 mb-4">
                      Full Distribution History ({troncAllocations.length} records)
                    </h4>

                    {troncAllocations.length === 0 ? (
                      <div className="bg-muted rounded-lg p-6 text-center">
                        <Icon
                          name="PiggyBank"
                          size={32}
                          className="mx-auto mb-3 text-muted-foreground"
                        />
                        <p className="text-muted-foreground">
                          No TRONC allocations recorded yet
                        </p>
                      </div>
                    ) : (
                      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">%</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Note</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {troncAllocations
                                .sort((a, b) => new Date(b.allocatedOn || b.createdOn || 0) - new Date(a.allocatedOn || a.createdOn || 0))
                                .map((alloc, idx) => (
                                  <tr key={alloc.id || idx} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                      {formatDate(alloc.allocatedOn || alloc.periodStart)}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-500">
                                      {alloc.periodStart ? `${formatDate(alloc.periodStart)}${alloc.periodEnd ? ` – ${formatDate(alloc.periodEnd)}` : ''}` : '—'}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-right font-semibold text-success font-data">
                                      {formatCurrency(alloc.amount)}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-right text-gray-500">
                                      {alloc.percentage != null ? `${alloc.percentage.toFixed(1)}%` : '—'}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-500">
                                      {alloc.isManualOverride && (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-700 mb-1">
                                          <Icon name="AlertTriangle" size={10} />
                                          Override
                                        </span>
                                      )}
                                      {alloc.overrideNote && (
                                        <p className="text-xs text-amber-600">{alloc.overrideNote}</p>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                              <tr>
                                <td colSpan="2" className="px-4 py-3 text-sm font-bold text-gray-900">
                                  Total ({troncAllocations.length} distributions)
                                </td>
                                <td className="px-4 py-3 text-sm text-right font-bold text-success font-data">
                                  {formatCurrency(troncAllocations.reduce((sum, a) => sum + (a.amount || 0), 0))}
                                </td>
                                <td colSpan="2"></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === "role" && (
            <RoleAssignment employee={employee} onUpdate={onUpdate} />
          )}

          {activeTab === "hours" && <ServiceHourTracking employee={employee} />}
        </div>
      </div>
    </>
  );
};

export default EmployeeDetailPanel;
